/**
 * Document Constants
 *
 * File upload constraints, accepted file types, and validation utilities.
 * Keep in sync with backend: app-server/documents/constants.py
 */

// 750MB max file upload size
export const MAX_FILE_SIZE = 750 * 1024 * 1024;

// maximum number of files allowed per upload
export const MAX_FILES_LIMIT = 25;

// minimum file size to consider a document as non-blank (in bytes)
export const MIN_MEANINGFUL_FILE_SIZE = 1024; // 1KB

// accepted file extensions
// IF YOU UPDATE THIS YOU MUST ALSO UPDATE `app-server/documents/constants.py`
export const ACCEPTED_FILE_EXTENSIONS = [
  ".csv",
  ".docx",
  ".txt",
  ".doc",
  ".msg",
  ".eml",
  ".pdf",
  ".xlsx",
  ".jpg",
  ".jpeg",
  ".png",
  ".zip",
  ".xls",
  ".xlsm",
  ".xltx",
  ".xltm",
] as const;

// file types string for HTML input accept attribute
export const ACCEPTED_FILE_TYPES = ACCEPTED_FILE_EXTENSIONS.join(",");

// MIME types for react-dropzone accept prop
export const ACCEPTED_MIME_TYPES = {
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/plain": [".txt"],
  "application/msword": [".doc"],
  "application/vnd.ms-outlook": [".msg"],
  "message/rfc822": [".eml"],
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/zip": [".zip"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [
    ".xltx",
  ],
  "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
};

// .mp4 is not in the static lists above: it is only accepted for organizations
// that have opted in via Organization.allow_mp4_uploads (exposed to the client on
// OrganizationSettingsResponse.allow_mp4_uploads). The helpers below fold it into
// the accept lists when that flag is on. Keep in sync with the server-side gate in
// app-server/documents/constants.py (get_allowed_file_extensions).
export const MP4_EXTENSION = ".mp4";
export const MP4_MIME_TYPE = "video/mp4";

// accepted extensions, optionally including .mp4 for opted-in organizations.
// The base case returns the shared constant directly (callers only read it);
// only the opt-in case allocates a new array.
export const getAcceptedFileExtensions = (
  allowMp4 = false,
): readonly string[] =>
  allowMp4
    ? [...ACCEPTED_FILE_EXTENSIONS, MP4_EXTENSION]
    : ACCEPTED_FILE_EXTENSIONS;

// accept string for the HTML input `accept` attribute
export const getAcceptedFileTypes = (allowMp4 = false): string =>
  getAcceptedFileExtensions(allowMp4).join(",");

// accept map for react-dropzone's `accept` prop.
//
// Each entry maps a MIME type to its extension(s), and the extension is
// deliberately kept in the value array: react-dropzone flattens this into
// "<mime>,<ext>,..." and matches a dragged file if EITHER the MIME or the
// extension matches (attr-accept). That makes the drag-drop path robust to
// browsers that report the "wrong" MIME for .mp4 (e.g. Safari sends
// video/quicktime) — the file is still accepted on its .mp4 extension.
export const getAcceptedMimeTypes = (
  allowMp4 = false,
): Record<string, string[]> =>
  allowMp4
    ? { ...ACCEPTED_MIME_TYPES, [MP4_MIME_TYPE]: [MP4_EXTENSION] }
    : ACCEPTED_MIME_TYPES;

// file extension validation function. `allowMp4` folds in .mp4 for opted-in
// organizations; it defaults to false so callers without org context keep the
// pre-existing (mp4-excluded) behaviour.
export const isValidFileExtension = (
  filename: string,
  allowMp4 = false,
): boolean => {
  const dotIndex = filename.lastIndexOf(".");
  // A filename with no dot has no extension to match. Guard explicitly:
  // substring(-1) would otherwise return the whole name rather than "".
  if (dotIndex === -1) return false;
  const extension = filename.toLowerCase().substring(dotIndex);
  return getAcceptedFileExtensions(allowMp4).includes(extension);
};

// file size formatting utility.
// Returns "—" for null/undefined or non-finite/negative inputs so callers can
// pass raw `file_size_bytes` columns (which may be null) without guarding.
const FILE_SIZE_UNITS = ["Bytes", "KB", "MB", "GB", "TB", "PB"] as const;

export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  // Clamp the unit index so values >= 1 PB don't overflow the units array
  // and render as "1234 undefined".
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    FILE_SIZE_UNITS.length - 1,
  );

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${FILE_SIZE_UNITS[i]}`;
};

// max file size in human readable format
export const MAX_FILE_SIZE_DISPLAY = formatFileSize(MAX_FILE_SIZE); // "750 MB"

// detect if a file is likely blank or empty based on size and type
export const isLikelyBlankDocument = (file: File): boolean => {
  // check if file is too small to contain meaningful content
  if (file.size < MIN_MEANINGFUL_FILE_SIZE) {
    return true;
  }

  // for PDFs, be more strict as blank PDFs can be larger due to metadata
  if (
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf")
  ) {
    // blank PDFs are typically under 5KB, but we'll be conservative
    return file.size < 2048; // 2KB
  }

  // for image files, very small files are likely blank or invalid
  if (file.type.startsWith("image/")) {
    return file.size < 512; // 512 bytes - very small for any meaningful image
  }

  // for text-based files (CSV, TXT), very small files are likely blank
  if (
    file.type === "text/plain" ||
    file.type === "text/csv" ||
    file.name.toLowerCase().endsWith(".txt") ||
    file.name.toLowerCase().endsWith(".csv")
  ) {
    return file.size < 10; // 10 bytes - basically empty
  }

  // for office documents, they have overhead even when blank, so check for very small sizes
  if (
    file.type.includes("officedocument") ||
    file.name.toLowerCase().endsWith(".docx") ||
    file.name.toLowerCase().endsWith(".xlsx")
  ) {
    return file.size < 4096; // 4KB - typical minimum for office docs with content
  }

  return false;
};

// get a user-friendly message for blank document detection
export const getBlankDocumentMessage = (filename: string): string => {
  // Mirror the no-dot guard in isValidFileExtension: a name with no dot has no
  // extension, so fall through to the generic message rather than letting
  // substring(-1) echo the whole filename.
  const dotIndex = filename.lastIndexOf(".");
  const extension =
    dotIndex === -1 ? "" : filename.toLowerCase().substring(dotIndex);

  switch (extension) {
    case ".pdf":
      return `"${filename}" appears to be blank or empty. Please upload a PDF with actual content.`;
    case ".jpg":
    case ".jpeg":
    case ".png":
      return `"${filename}" appears to be blank or corrupted. Please upload a valid image file.`;
    case ".txt":
    case ".csv":
      return `"${filename}" appears to be empty. Please upload a file with content.`;
    case ".docx":
    case ".doc":
    case ".xlsx":
    case ".xls":
      return `"${filename}" appears to be blank or corrupted. Please upload a document with actual content.`;
    default:
      return `"${filename}" appears to be blank or empty. Please upload a file with meaningful content.`;
  }
};
